/**
 * <ClientsProjectsSubTab /> — tabular view of *all* projects across every
 * client in the workspace, shown on Clients > Projects subtab.
 *
 * Re-uses AdvancedTable so users get search / group-by / column layout /
 * saved views / CSV export for free. Client name comes from a joined lookup.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtRelative } from "@/lib/format";
import AdvancedTable from "@/components/table/AdvancedTable";
import { FolderKanban } from "lucide-react";

const HEALTH_TONE = {
  on_track: "border-emerald-500/40 text-emerald-700 bg-emerald-500/5",
  at_risk: "border-amber-500/40 text-amber-700 bg-amber-500/5",
  off_track: "border-red-500/40 text-red-700 bg-red-500/5",
};

export default function ClientsProjectsSubTab() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    (async () => {
      const [p, c] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/projects`),
        api.get(`/workspaces/${activeWorkspaceId}/clients`),
      ]);
      const cmap = {};
      (c.data || []).forEach((x) => { cmap[x.id] = x; });
      setClients(cmap);
      setProjects(p.data || []);
      setLoaded(true);
    })();
  }, [activeWorkspaceId]);

  const rows = useMemo(() => projects.map((p) => ({
    ...p,
    client_name: clients[p.client_id]?.name || "—",
  })), [projects, clients]);

  const columns = [
    { key: "name", label: "Project", width: 260, sortable: true, filterType: "text", frozen: true,
      render: (r) => <span className="font-medium truncate">{r.name}</span> },
    { key: "client_name", label: "Client", width: 200, sortable: true, filterType: "text", groupable: true },
    { key: "status", label: "Status", width: 110, sortable: true, filterType: "select",
      filterOptions: [
        { value: "active", label: "Active" }, { value: "completed", label: "Completed" },
        { value: "archived", label: "Archived" }, { value: "canceled", label: "Cancelled" },
      ], groupable: true,
      render: (r) => <span className="text-xs uppercase tracking-wider">{r.status || "—"}</span> },
    { key: "health", label: "Health", width: 110, sortable: true, filterType: "select",
      filterOptions: [
        { value: "on_track", label: "On track" }, { value: "at_risk", label: "At risk" },
        { value: "off_track", label: "Off track" },
      ], groupable: true,
      render: (r) => (
        <span className={`text-[10px] uppercase tracking-wider border px-1.5 py-0.5 ${HEALTH_TONE[r.health] || ""}`}>
          {(r.health || "on_track").replace("_", " ")}
        </span>
      ),
    },
    { key: "estimated_hours", label: "Est. hrs", width: 90, sortable: true, align: "right", sum: true, type: "number" },
    { key: "actual_hours", label: "Actual", width: 90, sortable: true, align: "right", sum: true, type: "number" },
    { key: "planned_end_date", label: "Due", width: 120, sortable: true, align: "right",
      render: (r) => <span className="text-xs text-muted-foreground">{r.planned_end_date || "—"}</span> },
    { key: "created_at", label: "Created", width: 120, sortable: true, align: "right",
      render: (r) => <span className="text-xs text-muted-foreground">{fmtRelative(r.created_at)}</span> },
  ];

  return (
    <AdvancedTable
      tableKey="clients-projects"
      title="All projects"
      rows={rows}
      columns={columns}
      loading={!loaded}
      emptyIcon={FolderKanban}
      emptyTitle="No projects yet"
      emptyDescription="Projects created under any client show up here."
      onRowClick={(r) => navigate(`/app/projects/${r.id}`)}
    />
  );
}
